import { useAuth } from '../context/AuthContext';

/**
 * Hook to check user permissions
 * @returns {Object} Permission checking utilities
 */
export function usePermissions() {
  const { user } = useAuth();

  const permissions = user?.effective_permissions || {};

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission key (e.g., 'foncier.view')
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.is_superuser) return true;
    return permissions[permission] === true;
  };

  /**
   * Check if user has any of the specified permissions
   * @param {string[]} permissionList - Array of permission keys
   * @returns {boolean}
   */
  const hasAnyPermission = (permissionList) => {
    if (!user) return false;
    if (user.is_superuser) return true;
    return permissionList.some(p => permissions[p] === true);
  };

  /**
   * Check if user has all of the specified permissions
   * @param {string[]} permissionList - Array of permission keys
   * @returns {boolean}
   */
  const hasAllPermissions = (permissionList) => {
    if (!user) return false;
    if (user.is_superuser) return true;
    return permissionList.every(p => permissions[p] === true);
  };

  /**
   * Check if user can access a module (has view permission)
   * @param {string} module - Module name (e.g., 'foncier', 'contentieux')
   * @returns {boolean}
   */
  const canView = (module) => hasPermission(`${module}.view`);

  /**
   * Check if user can create in a module
   * Checks both 'create' (new) and 'add' (old) permission names
   * @param {string} module - Module name
   * @returns {boolean}
   */
  const canCreate = (module) => hasPermission(`${module}.create`) || hasPermission(`${module}.add`);

  /**
   * Check if user can edit in a module
   * Checks both 'edit' (new) and 'change' (old) permission names
   * @param {string} module - Module name
   * @returns {boolean}
   */
  const canEdit = (module) => hasPermission(`${module}.edit`) || hasPermission(`${module}.change`);

  /**
   * Check if user can delete in a module
   * @param {string} module - Module name
   * @returns {boolean}
   */
  const canDelete = (module) => hasPermission(`${module}.delete`);

  /**
   * Check if user can access admin panel
   * @returns {boolean}
   */
  const canAccessAdmin = () => {
    if (!user) return false;
    return user.is_superuser || user.can_access_admin || hasPermission('admin.view');
  };

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canAccessAdmin,
  };
}

export default usePermissions;
